import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit2, Trash2, Eye, Copy, RotateCcw, Archive, FileX, Building2, BookOpen, Award, X, Check, Loader2, AlertTriangle, CreditCard, FileEdit, Gift } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { formatDateTimeWithSeconds } from '../../utils/timeUtils';
import SearchWithHistory from '../../components/SearchWithHistory';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import QuizAnswerMode from '../quiz/QuizAnswerMode';
import { mapDbQuestionsToStandard } from '../../utils/questionMapper';

interface Paper {
  id: string;
  title: string;
  year: number | null;
  month: number | null;
  organization: string | null;
  category_type: string | null;
  level_text: string | null;
  paper_type: string | null;
  duration: number | null;
  is_published: boolean;
  is_free: boolean;
  status: string;
  single_choice_count: number | null;
  multi_choice_count: number | null;
  judge_count: number | null;
  coding_count: number | null;
  total_score: number | null;
  remark: string | null;
  created_at: string;
  updated_at: string | null;
}

function resolveCategoryType(raw: string | null): string | null {
  if (!raw) return null;
  return categoryStore.getCategoryNameByCode(raw) || raw;
}

// 判断分类名称是否属于"非编程类"（other），用于筛选时把所有 other 类分类归并匹配
// 规则：图形化→scratch、Python→python、C++/C语言→cpp，其余均为 other
function isOtherCategoryByName(name: string): boolean {
  if (name.includes('图形化')) return false;
  if (name.includes('Python')) return false;
  if (name.includes('C++') || name.includes('C语言')) return false;
  return true;
}

const FILTER_STORAGE_KEY = 'papers_filter_state';

interface FilterState {
  selectedOrganization: string;
  selectedCategory: string;
  selectedLevel: string;
  accessFilter: 'all' | 'free' | 'paid';
  searchTerm: string;
  currentPage: number;
}

// 从 localStorage 读取初始筛选状态（用于 useState 初始化函数）
function loadSavedFilterState(): FilterState {
  try {
    const savedState = localStorage.getItem(FILTER_STORAGE_KEY);
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (e) {
    // 忽略解析错误
  }
  return {
    selectedOrganization: '',
    selectedCategory: '',
    selectedLevel: '',
    accessFilter: 'all',
    searchTerm: '',
    currentPage: 1
  };
}

export default function Papers() {
  const navigate = useNavigate();
  const savedState = loadSavedFilterState();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(savedState.searchTerm);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingRemark, setEditingRemark] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [remarkValue, setRemarkValue] = useState('');
  const [showBatchClearRemarkModal, setShowBatchClearRemarkModal] = useState(false);
  const [clearingBatchRemark, setClearingBatchRemark] = useState(false);
  const [showBatchSetPaidModal, setShowBatchSetPaidModal] = useState(false);
  const [settingBatchPaid, setSettingBatchPaid] = useState(false);
  const [showBatchSetDraftModal, setShowBatchSetDraftModal] = useState(false);
  const [settingBatchDraft, setSettingBatchDraft] = useState(false);
  const [showBatchSetFreeModal, setShowBatchSetFreeModal] = useState(false);
  const [settingBatchFree, setSettingBatchFree] = useState(false);

  // 预览模态框相关状态
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPaperId, setPreviewPaperId] = useState<string | null>(null);
  const [previewPaperData, setPreviewPaperData] = useState<any>(null);
  const [previewQuestions, setPreviewQuestions] = useState<any[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 分页状态
  const [currentPage, setCurrentPage] = useState(savedState.currentPage);
  const pageSize = 10;

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>(savedState.selectedOrganization);
  const [selectedCategory, setSelectedCategory] = useState<string>(savedState.selectedCategory);
  const [selectedLevel, setSelectedLevel] = useState<string>(savedState.selectedLevel);
  const [accessFilter, setAccessFilter] = useState<'all' | 'free' | 'paid'>(savedState.accessFilter);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // 保存筛选状态到 localStorage（每次筛选条件变化时）
  useEffect(() => {
    const state: FilterState = {
      selectedOrganization,
      selectedCategory,
      selectedLevel,
      accessFilter,
      searchTerm,
      currentPage
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  }, [selectedOrganization, selectedCategory, selectedLevel, accessFilter, searchTerm, currentPage]);

  useEffect(() => {
    loadPapers();
    loadFilterData();
  }, []);

  const loadFilterData = async () => {
    setLoadingFilters(true);
    try {
      await categoryStore.init();
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    } catch (err) {
      
    } finally {
      setLoadingFilters(false);
    }
  };

  const filteredCategories = useMemo(() => {
    if (!selectedOrganization) return [];
    return categories.filter(c => c.orgId === selectedOrganization);
  }, [categories, selectedOrganization]);

  const filteredLevels = useMemo(() => {
    if (!selectedCategory) return [];
    return levels.filter(l => l.catId === selectedCategory);
  }, [levels, selectedCategory]);

  // 级联筛选校验：仅在 categories/levels 数据加载完成后执行
  useEffect(() => {
    if (selectedOrganization && categories.length > 0) {
      const cats = categories.filter(c => c.orgId === selectedOrganization);
      if (cats.length > 0 && selectedCategory && !cats.find(c => c.id === selectedCategory)) {
        setSelectedCategory('');
        setSelectedLevel('');
      }
    }
  }, [selectedOrganization, categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory && levels.length > 0) {
      const levs = levels.filter(l => l.catId === selectedCategory);
      if (levs.length > 0 && selectedLevel && !levs.find(l => l.id === selectedLevel)) {
        setSelectedLevel('');
      }
    }
  }, [selectedCategory, levels, selectedLevel]);

  const clearFilters = () => {
    setSelectedOrganization('');
    setSelectedCategory('');
    setSelectedLevel('');
    setAccessFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
    // 清除 localStorage 中的筛选状态
    localStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const loadPapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('papers')
        .select('*')
        .eq('status', 'published')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPapers(data || []);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleSelectAll = () => {
    const currentPageIds = paginatedPapers.map(p => p.id);
    const allCurrentPageSelected = currentPageIds.every(id => selectedIds.has(id));
    if (allCurrentPageSelected) {
      // 取消选中当前页的所有项
      const newSet = new Set(selectedIds);
      currentPageIds.forEach(id => newSet.delete(id));
      setSelectedIds(newSet);
    } else {
      // 选中当前页的所有项
      const newSet = new Set(selectedIds);
      currentPageIds.forEach(id => newSet.add(id));
      setSelectedIds(newSet);
    }
  };

  const startEditRemark = (paper: Paper) => {
    setEditingRemark(paper.id);
    setRemarkValue(paper.remark || '');
  };

  const saveRemark = async (id: string) => {
    setSavingId(id);
    try {
      await supabase
        .from('papers')
        .update({ remark: remarkValue, updated_at: new Date().toISOString() })
        .eq('id', id);
      setPapers(papers.map(p => p.id === id ? { ...p, remark: remarkValue } : p));
    } finally {
      setSavingId(null);
      setEditingRemark(null);
    }
  };

  const handleRemarkKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') saveRemark(id);
  };

  const togglePublishStatus = async (paper: Paper) => {
    const newStatus = !paper.is_published;
    if (!confirm(`确定${newStatus ? '发布' : '隐藏'}此试卷？`)) return;
    try {
      await supabase.from('papers').update({ is_published: newStatus }).eq('id', paper.id);
      setPapers(papers.map(p => p.id === paper.id ? { ...p, is_published: newStatus } : p));
    } catch (e) {
      
    }
  };

  const toggleFreeStatus = async (paper: Paper) => {
    const newStatus = !paper.is_free;
    if (!confirm(`确定${newStatus ? '设为免费' : '设为付费'}此试卷？`)) return;
    try {
      await supabase.from('papers').update({ is_free: newStatus }).eq('id', paper.id);
      setPapers(papers.map(p => p.id === paper.id ? { ...p, is_free: newStatus } : p));
    } catch (e) {
      
    }
  };

  const handleDelete = async (paper: Paper) => {
    if (!confirm('确定删除此试卷？删除后将移入回收站')) return;
    try {
      await supabase
        .from('papers')
        .update({
          deleted_at: new Date().toISOString(),
          original_status: paper.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', paper.id);
      setPapers(papers.filter(p => p.id !== paper.id));
    } catch (e) {
      
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) {
      alert('请先选择要删除的试卷');
      return;
    }
    if (!confirm(`确定删除选中的 ${selectedIds.size} 份试卷？删除后将移入回收站`)) return;
    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        const paper = papers.find(p => p.id === id);
        await supabase
          .from('papers')
          .update({
            deleted_at: new Date().toISOString(),
            original_status: paper?.status || 'published',
            updated_at: new Date().toISOString()
          })
          .eq('id', id);
      }
      setPapers(papers.filter(p => !selectedIds.has(p.id)));
      setSelectedIds(new Set());
      alert('批量删除成功，已移入回收站');
    } catch (e) {
      
      alert('批量删除失败');
    }
  };

  const handleCopy = async (paper: Paper) => {
    if (!confirm('确定复制此试卷？')) return;
    try {
      const { data: questions, error: questionsError } = await supabase.from('questions').select('*').eq('paper_id', paper.id);
      if (questionsError) {
        
        alert('加载原试卷题目失败');
        return;
      }

      const newRemark = paper.remark ? `${paper.remark}【副本】` : '【副本】';
      const newPaper = {
        title: paper.title,
        year: paper.year,
        month: paper.month,
        organization: paper.organization,
        category_type: paper.category_type,
        level_text: paper.level_text,
        paper_type: paper.paper_type,
        duration: paper.duration,
        total_score: paper.total_score,
        single_choice_count: paper.single_choice_count,
        multi_choice_count: paper.multi_choice_count,
        judge_count: paper.judge_count,
        coding_count: paper.coding_count,
        is_free: paper.is_free,
        is_published: false,
        status: 'draft',
        remark: newRemark
      };

      const { data: created, error: paperError } = await supabase.from('papers').insert(newPaper).select().single();
      if (paperError || !created) {
        
        alert('创建试卷失败');
        return;
      }

      if (questions && questions.length > 0) {
        const newQuestions = questions.map((q: any) => ({
          paper_id: created.id,
          content: q.content,
          question_type: q.question_type,
          score: q.score,
          order_index: q.order_index,
          options: q.options || [],
          correct_answer: q.correct_answer,
          explanation: q.explanation,
          reference_answer: q.reference_answer,
          scoring_points: q.scoring_points,
          title_images: q.title_images || [],
          status: 'draft'
        }));

        const { error: insertError } = await supabase.from('questions').insert(newQuestions);
        if (insertError) {
          
          alert('插入题目失败');
          return;
        }
      }

      alert('复制成功');
      loadPapers();
    } catch (e: any) {
      
      alert('复制失败: ' + (e?.message || '未知错误'));
    }
  };

  const moveToDraft = async (paper: Paper) => {
    if (!confirm('确定将此试卷转为草稿？')) return;
    try {
      await supabase
        .from('papers')
        .update({ status: 'draft', is_published: false, updated_at: new Date().toISOString() })
        .eq('id', paper.id);
      setPapers(papers.filter(p => p.id !== paper.id));
      alert('已转为草稿');
    } catch (e) {
      
      alert('转为草稿失败');
    }
  };

  const handleView = async (paper: Paper) => {
    // 打开预览模态框并加载数据
    setPreviewPaperId(paper.id);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    try {
      // 加载试卷数据
      const { data: paperData } = await supabase.from('papers').select('*').eq('id', paper.id).single();
      if (paperData) {
        setPreviewPaperData(paperData);
        // 加载题目数据
        const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paper.id).order('order_index', { ascending: true });
        // 直接存储 DB 原始数据，由 mapDbQuestionsToStandard 统一映射
        setPreviewQuestions(questionsData || []);
      }
    } catch (error) {
      console.error('加载预览数据失败:', error);
    } finally {
      setPreviewLoading(false);
    }
  };
  const resetFilters = () => setSearchTerm('');

  const generateDisplayTitle = (paper: Paper) => {
    if (paper.title) return paper.title;
    const parts: string[] = [];
    if (paper.year) parts.push(`${paper.year}年`);
    if (paper.month) parts.push(`${paper.month}月`);
    if (paper.organization) parts.push(paper.organization);
    if (paper.category_type) {
      const categoryName = categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type;
      parts.push(categoryName);
    }
    if (paper.level_text) parts.push(paper.level_text);
    if (paper.paper_type) {
      parts.push(paper.paper_type === 'real' ? '真题' : '模拟题');
    }
    return parts.length > 0 ? parts.join('') : '未命名试卷';
  };

  const formatDate = (dateStr: string | null) => {
    return formatDateTimeWithSeconds(dateStr);
  };

  const filteredPapers = papers.filter(p => {
    if (accessFilter !== 'all') {
      if (accessFilter === 'free' && !p.is_free) return false;
      if (accessFilter === 'paid' && p.is_free) return false;
    }
    const resolvedType = resolveCategoryType(p.category_type);
    // 试卷是否属于 other 类型（category_type 为 'other' 或解析后仍为 'other'）
    const paperIsOther = p.category_type === 'other' || resolvedType === 'other';
    if (selectedOrganization) {
      const cat = categories.find(c => c.name === resolvedType || c.id === p.category_type);
      // other 类试卷：只要选中组织下存在任意 other 类分类即视为匹配
      if ((!cat || cat.orgId !== selectedOrganization) && paperIsOther) {
        const orgHasOther = categories.some(c => c.orgId === selectedOrganization && isOtherCategoryByName(c.name));
        if (orgHasOther) {
          // 继续后续 selectedCategory 判断
        } else {
          return false;
        }
      } else if (!cat || cat.orgId !== selectedOrganization) {
        return false;
      }
    }
    if (selectedCategory) {
      const selectedCat = categories.find(c => c.id === selectedCategory);
      if (!selectedCat) return false;
      // 精确匹配：分类名或 id 直接相等
      const exactMatch = resolvedType === selectedCat.name || p.category_type === selectedCategory;
      if (exactMatch) {
        // 命中精确匹配
      } else if (paperIsOther && isOtherCategoryByName(selectedCat.name)) {
        // other 类试卷 + 选中的也是 other 类分类 → 视为匹配
      } else {
        return false;
      }
    }
    if (selectedLevel) {
      const level = levels.find(l => l.id === selectedLevel);
      if (!level || p.level_text !== level.name) return false;
    }
    if (!searchTerm.trim()) return true;
    const searchTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
    if (searchTerms.length === 0) return true;
    const title = (p.title || '').toLowerCase();
    const remark = (p.remark || '').toLowerCase();
    return searchTerms.every(term => title.includes(term) || remark.includes(term));
  });

  // 分页计算
  const totalPages = Math.ceil(filteredPapers.length / pageSize);
  const paginatedPapers = filteredPapers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // 当筛选条件变化时重置到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedOrganization, selectedCategory, selectedLevel, accessFilter]);

  return (
    <div className="p-6 bg-[#f0f7ff] min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">试卷列表</h1>

      {/* 三级级联筛选 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="space-y-4">
          {/* 考评体系 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">考评体系</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {loadingFilters ? (
                <div className="px-4 py-2 text-sm text-gray-400">加载中...</div>
              ) : organizations.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无考评体系</div>
              ) : (
                organizations.map(org => (
                  <motion.button
                    key={org.id}
                    onClick={() => { setSelectedOrganization(org.id); setSelectedCategory(''); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedOrganization === org.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {org.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 课程分类 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">课程分类</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedOrganization ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择考评体系</div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无课程分类</div>
              ) : (
                filteredCategories.map(cat => (
                  <motion.button
                    key={cat.id}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedLevel(''); }}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {cat.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 级别 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">级别</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {!selectedCategory ? (
                <div className="px-4 py-2 text-sm text-gray-400">请先选择课程分类</div>
              ) : filteredLevels.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-400">暂无级别</div>
              ) : (
                filteredLevels.map(level => (
                  <motion.button
                    key={level.id}
                    onClick={() => setSelectedLevel(level.id)}
                    className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                      selectedLevel === level.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {level.name}
                  </motion.button>
                ))
              )}
            </div>
          </div>

          {/* 清空筛选 */}
          {(selectedOrganization || selectedCategory || selectedLevel) && (
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                清空筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <SearchWithHistory
          storageKey="papers_search_history"
          placeholder="搜索试卷名称或备注（支持空格分隔多条件）"
          value={searchTerm}
          onChange={setSearchTerm}
          onSearch={() => {}}
          onReset={resetFilters}
        />
        <select
          value={accessFilter}
          onChange={(e) => setAccessFilter(e.target.value as 'all' | 'free' | 'paid')}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">全部</option>
          <option value="paid">付费</option>
          <option value="free">免费</option>
        </select>
        <button onClick={clearFilters} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />重置筛选
        </button>
        <button onClick={() => navigate('/admin/recycle-bin')} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2">
          <Archive className="w-4 h-4" />回收站
        </button>
        {selectedIds.size > 0 && (
          <>
            <button
              onClick={() => setShowBatchSetDraftModal(true)}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <FileEdit className="w-4 h-4" />
              批量移为草稿({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchSetFreeModal(true)}
              className="px-4 py-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 flex items-center gap-2"
            >
              <Gift className="w-4 h-4" />
              批量设为免费({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchSetPaidModal(true)}
              className="px-4 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 flex items-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              批量设为付费({selectedIds.size})
            </button>
            <button
              onClick={() => setShowBatchClearRemarkModal(true)}
              className="px-4 py-2 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              批量清除备注({selectedIds.size})
            </button>
            <button onClick={handleBatchDelete} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />批量删除({selectedIds.size})
            </button>
          </>
        )}
      </div>

      {!loading && (
        <div className="mb-3 text-sm text-gray-600">
          {searchTerm.trim() ? (
            filteredPapers.length === 0 ? (
              <span>未找到匹配的试卷</span>
            ) : (
              <span>共找到 <span className="font-medium text-blue-600">{filteredPapers.length}</span> 份试卷</span>
            )
          ) : (
            <span>共 <span className="font-medium text-gray-900">{filteredPapers.length}</span> 份试卷</span>
          )}
        </div>
      )}

      {loading ? <div className="text-center py-8">加载中...</div> : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#e8f4ff]">
              <tr>
                <th className="px-3 py-3 text-left"><input type="checkbox" checked={paginatedPapers.length > 0 && paginatedPapers.every(p => selectedIds.has(p.id))} onChange={toggleSelectAll} className="rounded border-gray-300" /></th>
                <th className="px-3 py-3 text-left text-sm font-medium text-blue-900">试卷名称</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">备注</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">单选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">多选</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">判断</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">非客观</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">总分</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">时长</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">状态</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">访问</th>
                <th className="px-2 py-3 text-center text-sm font-medium text-blue-900">最后更新</th>
                <th className="px-3 py-3 text-center text-sm font-medium text-blue-900">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedPapers.map((paper) => (
                <motion.tr key={paper.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-gray-50">
                  <td className="px-3 py-3"><input type="checkbox" checked={selectedIds.has(paper.id)} onChange={() => toggleSelect(paper.id)} className="rounded border-gray-300" /></td>
                  <td className="px-3 py-3"><span onClick={() => handleView(paper)} className="text-sm text-blue-600 font-medium cursor-pointer hover:underline">{generateDisplayTitle(paper)}</span></td>
                  <td className="px-2 py-3 text-center">
                    {editingRemark === paper.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={remarkValue}
                          onChange={(e) => setRemarkValue(e.target.value)}
                          onBlur={() => saveRemark(paper.id)}
                          onKeyDown={(e) => handleRemarkKeyDown(e, paper.id)}
                          className="w-24 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        {remarkValue && (
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setRemarkValue('');
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="清除备注"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {savingId === paper.id ? (
                          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    ) : (
                      <span onClick={() => startEditRemark(paper)} className="text-sm text-gray-600 cursor-pointer hover:text-blue-600 hover:underline">
                        {savingId === paper.id ? '保存中...' : (paper.remark || '-')}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.single_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.multi_choice_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.judge_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.coding_count || 0}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.total_score || '-'}</td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{paper.duration ? `${paper.duration}分钟` : '-'}</td>
                  <td className="px-2 py-3 text-center">
                    <button onClick={() => togglePublishStatus(paper)} className={`px-2 py-0.5 text-xs font-medium rounded-full ${paper.is_published ? 'text-green-600 bg-green-50' : 'text-gray-600 bg-gray-100'}`}>
                      {paper.is_published ? '发布' : '隐藏'}
                    </button>
                  </td>
                  <td className="px-2 py-3 text-center">
                    <button onClick={() => toggleFreeStatus(paper)} className={`px-2 py-0.5 text-xs font-medium rounded-full ${paper.is_free ? 'text-green-700 bg-green-100 border border-green-200' : 'text-amber-600 bg-amber-50 border border-amber-200'}`}>
                      {paper.is_free ? '免费' : '付费'}
                    </button>
                  </td>
                  <td className="px-2 py-3 text-center text-sm text-gray-600">{formatDate(paper.updated_at)}</td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleView(paper)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="查看"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => navigate(`/admin/paper-editor?edit=${paper.id}&from=papers`)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="编辑"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => moveToDraft(paper)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded" title="转为草稿"><FileX className="w-4 h-4" /></button>
                      <button onClick={() => handleCopy(paper)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="复制"><Copy className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(paper)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="删除"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          {filteredPapers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>{searchTerm.trim() ? '未找到匹配的试卷' : '暂无数据'}</p>
              {searchTerm.trim() && (
                <p className="text-sm text-gray-400 mt-2">尝试使用其他关键词或空格分隔多个条件</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 分页器 */}
      {totalPages > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              共 <span className="font-medium text-gray-900">{filteredPapers.length}</span> 条记录，
              每页 <span className="font-medium text-gray-900">{pageSize}</span> 条，
              第 <span className="font-medium text-gray-900">{currentPage}</span> / {totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                首页
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                末页
              </button>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showBatchClearRemarkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !clearingBatchRemark && setShowBatchClearRemarkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <AlertTriangle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量清除备注</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要清除选中的 <span className="font-semibold text-orange-600">{selectedIds.size}</span> 个试卷的备注吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  此操作将清空这些试卷的备注内容，清除后无法恢复。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchClearRemarkModal(false)}
                    disabled={clearingBatchRemark}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setClearingBatchRemark(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              remark: '',
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setSelectedIds(new Set());
                        setShowBatchClearRemarkModal(false);
                        loadPapers();
                        alert('批量清除备注成功');
                      } catch (err) {
                        
                        alert('批量清除备注失败');
                      } finally {
                        setClearingBatchRemark(false);
                      }
                    }}
                    disabled={clearingBatchRemark}
                    className="px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {clearingBatchRemark ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        清除中...
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        确认清除
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBatchSetPaidModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !settingBatchPaid && setShowBatchSetPaidModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-100 rounded-full">
                    <CreditCard className="w-6 h-6 text-amber-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量设为付费</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要将选中的 <span className="font-semibold text-amber-600">{selectedIds.size}</span> 份试卷设为付费吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  此操作将把这些试卷的访问权限设为付费，用户需要购买后才能访问。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchSetPaidModal(false)}
                    disabled={settingBatchPaid}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setSettingBatchPaid(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              is_free: false,
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setPapers(papers.map(p => selectedIds.has(p.id) ? { ...p, is_free: false } : p));
                        setSelectedIds(new Set());
                        setShowBatchSetPaidModal(false);
                        alert('批量设为付费成功');
                      } catch (err) {
                        
                        alert('批量设为付费失败');
                      } finally {
                        setSettingBatchPaid(false);
                      }
                    }}
                    disabled={settingBatchPaid}
                    className="px-4 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {settingBatchPaid ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        设置中...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        确认设为付费
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量移为草稿确认弹窗 */}
      <AnimatePresence>
        {showBatchSetDraftModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !settingBatchDraft && setShowBatchSetDraftModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <FileEdit className="w-6 h-6 text-gray-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量移为草稿</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要将选中的 <span className="font-semibold text-gray-700">{selectedIds.size}</span> 份试卷移为草稿吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  此操作将把已发布的试卷状态改为草稿，用户将无法访问这些试卷。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchSetDraftModal(false)}
                    disabled={settingBatchDraft}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setSettingBatchDraft(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              is_published: false,
                              status: 'draft',
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        // 从列表中移除已移为草稿的试卷（因为列表只显示已发布的）
                        setPapers(papers.filter(p => !selectedIds.has(p.id)));
                        setSelectedIds(new Set());
                        setShowBatchSetDraftModal(false);
                        alert('批量移为草稿成功');
                      } catch (err) {
                        alert('批量移为草稿失败');
                      } finally {
                        setSettingBatchDraft(false);
                      }
                    }}
                    disabled={settingBatchDraft}
                    className="px-4 py-2 text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {settingBatchDraft ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        处理中...
                      </>
                    ) : (
                      <>
                        <FileEdit className="w-4 h-4" />
                        确认移为草稿
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量设为免费确认弹窗 */}
      <AnimatePresence>
        {showBatchSetFreeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !settingBatchFree && setShowBatchSetFreeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Gift className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">确认批量设为免费</h2>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要将选中的 <span className="font-semibold text-green-600">{selectedIds.size}</span> 份试卷设为免费吗？
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  此操作将把这些试卷的访问权限设为免费，用户无需购买即可访问。
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBatchSetFreeModal(false)}
                    disabled={settingBatchFree}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={async () => {
                      setSettingBatchFree(true);
                      try {
                        const now = new Date().toISOString();
                        for (const id of selectedIds) {
                          await supabase
                            .from('papers')
                            .update({
                              is_free: true,
                              updated_at: now
                            })
                            .eq('id', id);
                        }
                        setPapers(papers.map(p => selectedIds.has(p.id) ? { ...p, is_free: true } : p));
                        setSelectedIds(new Set());
                        setShowBatchSetFreeModal(false);
                        alert('批量设为免费成功');
                      } catch (err) {
                        alert('批量设为免费失败');
                      } finally {
                        setSettingBatchFree(false);
                      }
                    }}
                    disabled={settingBatchFree}
                    className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {settingBatchFree ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        设置中...
                      </>
                    ) : (
                      <>
                        <Gift className="w-4 h-4" />
                        确认设为免费
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 试卷预览模态框 */}
      <AnimatePresence>
        {showPreviewModal && previewPaperData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <QuizAnswerMode
                  paper={{
                    id: previewPaperId || '',
                    title: previewPaperData.title || '试卷预览',
                    total_score: previewPaperData.total_score || 0,
                    category_type: previewPaperData.category_type || '',
                    description: previewPaperData.description || '',
                    remark: previewPaperData.remark || '',
                    year: previewPaperData.year,
                    month: previewPaperData.month,
                    organization: previewPaperData.organization,
                    source: previewPaperData.source,
                    nature: previewPaperData.nature,
                    level_text: previewPaperData.level_text,
                    paper_type: previewPaperData.paper_type,
                    duration: previewPaperData.duration || 60
                  }}
                  examQuestions={mapDbQuestionsToStandard(previewQuestions)}
                  currentUser={null}
                  paperId={previewPaperId || undefined}
                  isPreview={true}
                  isEditorPreview={true}
                  canScaleImages={true}
                  onBack={() => setShowPreviewModal(false)}
                  onExamQuestionsChange={(updatedQuestions) => {
                    // 预览模式保存缩放后，同步更新本地 previewQuestions state
                    // 这样下次重新打开预览时（即使不重新查询数据库）也能显示最新数据
                    setPreviewQuestions(prev => {
                      return prev.map(dbQ => {
                        const updated = updatedQuestions.find(u => u.id === dbQ.id);
                        if (!updated) return dbQ;
                        return {
                          ...dbQ,
                          // 关键修复：同步更新 content 字段，确保 HTML 内嵌图片的宽度持久化
                          // buildStandardFields 优先从 content 提取宽度，必须保证 content 与 title_image_widths 一致
                          content: updated.content || dbQ.content,
                          title_image_widths: updated.titleImageWidths || dbQ.title_image_widths,
                          title_image_layout: (updated as any).titleImageLayout || dbQ.title_image_layout,
                          image_position: (updated as any).imagePosition || dbQ.image_position,
                          option_layout: (updated as any).optionLayout || dbQ.option_layout,
                          options: updated.options || dbQ.options,
                          option_image_widths: (updated as any).optionImageWidths || dbQ.option_image_widths,
                          // 补全字段同步：确保题干图片修改不会被部分同步覆盖
                          title_images: updated.titleImages || dbQ.title_images,
                          reference_answer: (updated as any).answerText || dbQ.reference_answer,
                          reference_answer_images: (updated as any).referenceAnswerImages || dbQ.reference_answer_images,
                          reference_answer_image_widths: (updated as any).referenceAnswerImageWidths || dbQ.reference_answer_image_widths,
                          scoring_points: (updated as any).scoringPoints || dbQ.scoring_points,
                          scoring_points_images: (updated as any).scoringPointsImages || dbQ.scoring_points_images,
                          scoring_points_image_widths: (updated as any).scoringPointsImageWidths || dbQ.scoring_points_image_widths,
                          explanation: updated.explanation || dbQ.explanation,
                        };
                      });
                    });
                  }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}